import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth, AUTH_URL, DEMO, roleHome } from "@/context/AuthContext";
import AuthCallback from "@/webapp/auth/AuthCallback";
import Dashboard from "@/webapp/Dashboard";
import Onboarding from "@/webapp/auth/Onboarding";
import Verify from "@/webapp/government/VerifyConsole";
import VerifyReceipt from "@/webapp/government/VerifyReceipt";
import SiteOwnerConsole from "@/webapp/site-owners/SiteOwnerConsole";
import FleetOwnerConsole from "@/webapp/fleet-owners/FleetOwnerConsole";
import DriverFinder from "@/webapp/drivers/DriverFinder";

const SELF_AUTH_PATHS = ["/owners", "/fleet", "/verify-receipt", "/auth/callback"];

function Loader() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-white" data-testid="auth-loader">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-[#FF5722]/25 border-t-[#FF5722]" />
    </div>
  );
}

function BounceToAuth({ keepPath = false }) {
  useEffect(() => {
    if (DEMO) return;
    const here = `${window.location.pathname}${window.location.search}`;
    const q = keepPath && here !== "/" ? `?next=${encodeURIComponent(here)}` : "";
    window.location.replace(`${AUTH_URL}/login${q}`);
  }, [keepPath]);
  return <Loader />;
}

function DemoAutoLogin() {
  const { user, login } = useAuth();
  useEffect(() => {
    if (!DEMO || SELF_AUTH_PATHS.some((p) => window.location.pathname.startsWith(p))) return;
    if (user === null) login(process.env.REACT_APP_DEMO_EMAIL, process.env.REACT_APP_DEMO_PASSWORD);
  }, [user, login]);
  return null;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <BounceToAuth keepPath />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function RequireUser({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <BounceToAuth keepPath />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  return <Navigate to={roleHome(user)} replace />;
}

function OnboardingRoute() {
  const { user } = useAuth();
  if (user === undefined) return <Loader />;
  if (!user) return <BounceToAuth />;
  if (user.onboarding_completed) return <Navigate to="/dashboard" replace />;
  return <Onboarding />;
}

function GovRoute() {
  const { user } = useAuth();
  const allowed = user.role === "admin" || (user.role === "gov_agency" && user.gov_status === "verified");
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <Dashboard forceGov />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={DEMO ? <Navigate to="/gov" replace /> : <Protected><RoleHome /></Protected>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/gov" element={<Protected><GovRoute /></Protected>} />
      <Route path="/site-owners" element={<RequireUser><SiteOwnerConsole /></RequireUser>} />
      <Route path="/siteowner" element={<Navigate to="/site-owners" replace />} />
      <Route path="/fleet-owners" element={<RequireUser><FleetOwnerConsole /></RequireUser>} />
      <Route path="/drivers" element={<RequireUser><DriverFinder /></RequireUser>} />
      <Route path="/verify" element={<Protected><Verify /></Protected>} />
      <Route path="/verify-receipt" element={<VerifyReceipt />} />
      <Route path="/login" element={<BounceToAuth />} />
      <Route path="*" element={<BounceToAuth />} />
    </Routes>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <DemoAutoLogin />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
